// Runtime brand theming. A single primary hex (Settings → theme_primary) drives
// the whole `primary-*` palette: we keep its hue + saturation and generate the
// 50→900 ramp across a fixed lightness scale, then write RGB triplets to the CSS
// variables that tailwind.config.js maps `primary` onto. Semantic role colors
// (success/danger/…) are deliberately NOT themed — they keep their meanings.

// Target lightness (%) per shade — mirrors the feel of Tailwind's default ramps.
const SHADE_LIGHTNESS = {
  50: 97, 100: 94, 200: 86, 300: 77, 400: 66,
  500: 57, 600: 49, 700: 41, 800: 34, 900: 27,
};

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function hexToRgb(hex) {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s * 100, l * 100];
}

function hslToRgb(h, s, l) {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

/**
 * Build the full primary ramp from a base hex.
 * Returns { 50: 'r g b', 100: 'r g b', ... } (space-separated triplets).
 */
export function buildPrimaryRamp(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [h, s] = rgbToHsl(rgb);
  // Keep the picked hue; nudge saturation up a touch for light tints so pale
  // shades don't wash out to gray, and keep it lively for dark shades.
  const ramp = {};
  for (const [shade, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    const sat = clamp(s, 30, 95);
    const [r, g, b] = hslToRgb(h, sat, lightness);
    ramp[shade] = `${r} ${g} ${b}`;
  }
  return ramp;
}

/**
 * Apply a primary hex to the document by setting the --color-primary-* variables.
 * No-op for an invalid hex (keeps the CSS default). Returns true if applied.
 */
export const THEME_STORAGE_KEY = 'pos_theme_primary';

export function applyTheme(hex) {
  const ramp = buildPrimaryRamp(hex);
  if (!ramp) return false;
  const root = document.documentElement;
  for (const [shade, triplet] of Object.entries(ramp)) {
    root.style.setProperty(`--color-primary-${shade}`, triplet);
  }
  // Keep the browser chrome / mobile status-bar color (the <meta name="theme-color">)
  // in sync with the brand color so the titlebar recolors too.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', String(hex).trim());
  // Cache so a hard refresh restores the color instantly (before the async
  // settings fetch resolves) — no orange flash / no perceived revert.
  try { localStorage.setItem(THEME_STORAGE_KEY, String(hex).trim()); } catch { /* ignore */ }
  return true;
}

/**
 * Re-apply the last-known brand color from localStorage, if any. Call this once
 * at startup (before React renders) so the theme is present on the very first
 * paint; the authoritative value from Settings is applied a moment later.
 */
export function bootstrapTheme() {
  try {
    const cached = localStorage.getItem(THEME_STORAGE_KEY);
    if (cached) applyTheme(cached);
  } catch { /* ignore */ }
}
